import type { InfluencerShareCodesQuery } from '#graphql-operations'

type Influencer = NonNullable<NonNullable<InfluencerShareCodesQuery['me']>['influencer']>
interface ShareCodes {
  key: string
  codes: Influencer['commissionSettings']
}

/** A small, private read shared across page navigation; guests never request it. */
export function useInfluencerShareCodes() {
  const auth = useAuth()
  const { locale } = useI18n()
  const customerId = computed(() => auth?.activeCustomer.value?.id)
  const key = computed(() => `influencer-share:${customerId.value ?? 'guest'}:${locale.value}`)

  const { data } = useLazyAsyncData<ShareCodes>(key, async (): Promise<ShareCodes> => {
    const requestKey = key.value
    const requestCustomerId = customerId.value
    if (!requestCustomerId)
      return { key: requestKey, codes: [] }

    const result = await useGraphqlQuery('influencerShareCodes')
    const influencer = result.data?.me?.influencer
    return {
      key: requestKey,
      codes: result.data?.activeCustomer?.id === requestCustomerId && influencer && !influencer.disabled
        ? influencer.commissionSettings
        : [],
    }
  }, { server: false })

  // AsyncData can retain the previous key's result while the next one loads.
  // Hide it immediately on logout, customer change, or a market change.
  const codes = computed(() => {
    if (!customerId.value || data.value?.key !== key.value)
      return []
    const unique = new Map(data.value.codes
      .filter(code => code.couponCode.trim())
      .map(code => [code.couponCode, code]))
    return [...unique.values()]
  })

  return { codes }
}
